// Centralises how API errors / successes get turned into user-facing
// strings. Backend errors carry an optional stable code (`res_code`)
// that maps to an i18n key under `apiErrors.<code>`. When a code is
// absent or unknown, we fall back to the BE's English `message`.
//
// Usage in mutation hooks:
//
//   const { t } = useTranslation()
//   onError: (e) => toast.error(resolveApiError(e, t))

import type { TFunction } from "i18next"

import {
  extractApiErrorCode,
  extractApiErrorMessage,
} from "@/lib/api/client"

// Resolves an API error into a localised string.
//
// Lookup order:
//   1. `apiErrors.<res_code>` if the BE attached one
//   2. The BE's English `message` (already user-facing)
//   3. A generic fallback string
//
// Pass any `t` from `useTranslation()` — codes are resolved against the
// implicit translation namespace, so we explicitly look in `common`
// (where the registry lives).
export function resolveApiError(error: unknown, t: TFunction): string {
  const code = extractApiErrorCode(error)
  if (code) {
    const key = `apiErrors.${code}`
    const translated = t(key, { ns: "common", defaultValue: "" })
    if (translated && translated !== key) return translated
  }
  const message = extractApiErrorMessage(error)
  if (message) return message
  return t("errors.generic", { ns: "common", defaultValue: "Something went wrong" })
}

// Mirror for success codes — pass the BE response's `res_code` and the
// English `message` so the toast reads in the user's language when a
// code is registered, or falls back to the message otherwise.
export function resolveApiSuccess(
  code: string | undefined,
  message: string | undefined,
  t: TFunction,
): string {
  if (code) {
    const key = `apiSuccess.${code}`
    const translated = t(key, { ns: "common", defaultValue: "" })
    if (translated && translated !== key) return translated
  }
  return message ?? t("toast.saved", { ns: "common", defaultValue: "Saved" })
}
